import { MediaType } from "../../enums";
import { getPlayable, TimeHelper } from "../../helpers";
import { IMediaModel } from "../../models";

export type PlayableInfo = {
  isPlayable: boolean;
  isAvailable?: boolean;
  isComingSoon?: boolean;
  isExpiring?: boolean;
  availableFrom?: string;
  availableTo?: string;
};

export type PlayableConfig = {
  dateFormat: string;
  daysToExpire: number;
};

export const defaultPlayableConfig: PlayableConfig = {
  dateFormat: "DD. MMMM",
  daysToExpire: 14,
};

const formatDate = (date: Date | undefined, format: string) => {
  return TimeHelper.format(date || 0, format).toLowerCase();
};

const getAvailableFrom = (date: Date | undefined, format: string) => {
  return TimeHelper.isAfterCurrent(date) ? formatDate(date, format) : undefined;
};

const getAvailableTo = (
  date: Date | undefined,
  daysToExpire: number,
  format: string
) => {
  if (!date) return undefined;

  const daysRemaining = TimeHelper.diff(date, TimeHelper.getCurrentDateTime());
  const expiresSoon = daysRemaining >= 0 && daysRemaining <= daysToExpire;
  return expiresSoon ? formatDate(date, format) : undefined;
};

const buildPlayableInfo = (
  playable: boolean | undefined,
  from: Date | undefined,
  to: Date | undefined,
  config: PlayableConfig
): PlayableInfo => {
  const availableFrom = getAvailableFrom(from, config.dateFormat);
  const availableTo = getAvailableTo(
    to,
    config.daysToExpire,
    config.dateFormat
  );

  const isPlayable = !!playable;
  const isAvailable = to ? TimeHelper.isAfterCurrent(to) : false;
  const isComingSoon = !!(!isPlayable && availableFrom);
  const isExpiring = !!(isPlayable && availableTo);

  return {
    isPlayable,
    isAvailable,
    isComingSoon,
    isExpiring,
    availableFrom,
    availableTo,
  };
};
export const useIsPlayableWithDate = (
  media?: IMediaModel,
  override?: Partial<PlayableConfig>
): PlayableInfo => {
  if (!media) return { isPlayable: false };

  const config: PlayableConfig = {
    ...defaultPlayableConfig,
    ...override,
  };

  const { IsPlayable, AvailableFrom, AvailableTo, MediaTypeCode } = media;

  switch (MediaTypeCode) {
    case MediaType.Video:
    case MediaType.Channel:
    case MediaType.Series:
    case MediaType.Episode:
      return buildPlayableInfo(IsPlayable, AvailableFrom, AvailableTo, config);
    case MediaType.Collection: {
      // If collection has media, find the first playable
      const playable = media.Media?.find(getPlayable)?.IsPlayable ?? IsPlayable;
      return buildPlayableInfo(playable, AvailableFrom, AvailableTo, config);
    }
    default:
      return { isPlayable: false };
  }
};
